import { useEffect, useRef, useState } from 'react';

import { ArrowLeft, EllipsisVertical } from 'lucide-react';

import ChatbotIcon from '#/assets/svgs/ChatbotIcon.svg';
import Whatsapp from '#/assets/svgs/Whatsapp.svg';
import { Loader } from '#/common/components';
import { RESET_CHAT } from '#/utils/constants';
import { CHAT_RESET_SUCCESSFULLY, FAILED_CHAT_MESSAGE } from '#/utils/messages';

function ChatbotHeader({
  botName,
  onlineStatus,
  chatBotImage,
  whatsappAdded,
  whatsappNumber,
  isResponsive,
  onClose,
  messages,
  onResetChatAsync,
  onResetChatLoading,
  onShowAlert,
  isSending,
}) {
  const menuRef = useRef(null);

  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);

  const toggleMenu = () => setIsMenuOpen(!isMenuOpen);

  const handleMenuAction = action => () => {
    if (action === RESET_CHAT) {
      setIsResetModalOpen(true);
    }
    setIsMenuOpen(false);
  };

  const handleCloseResetModal = () => setIsResetModalOpen(false);

  const showAlert = (message, variant) => {
    setIsResetModalOpen(false);
    onShowAlert && onShowAlert(message, variant);
  };

  const confirmResetChat = async () => {
    try {
      await onResetChatAsync();
      showAlert(CHAT_RESET_SUCCESSFULLY, 'success');
    } catch (error) {
      const { response: { data } = {} } = error || {};
      const { detail = '' } = data || {};
      const errorMessage = detail || FAILED_CHAT_MESSAGE;
      showAlert(errorMessage, 'error');
    }
  };

  useEffect(() => {
    const handleClickOutside = event => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  return (
    <div className="header">
      <div className="avatar-detail">
        {isResponsive && (
          <span onClick={onClose} className="back-icon">
            <ArrowLeft />
          </span>
        )}
        <span className="image">{chatBotImage ? <img src={chatBotImage} alt="Chatbot" /> : <ChatbotIcon />}</span>
        <div className="detail">
          <h5>{botName}</h5>
          <p>{onlineStatus}</p>
        </div>
      </div>
      <div className="whatsapp-icon">
        {whatsappAdded && (
          <a href={`https://wa.me/${whatsappNumber}`} target="_blank">
            <Whatsapp />
          </a>
        )}
        <div className="menu-container" ref={menuRef}>
          <span className={`${messages?.length === 0 || isSending ? 'disabled' : ''} menu-icon`} onClick={toggleMenu}>
            <EllipsisVertical />
          </span>
          {isMenuOpen && (
            <div className="popup-menu">
              <div className="menu-item" onClick={handleMenuAction(RESET_CHAT)}>
                Reset Chat
              </div>
            </div>
          )}
        </div>
      </div>
      {isResetModalOpen && (
        <div className="modal-overlay" onClick={handleCloseResetModal}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h4>Reset Chat</h4>
            <p>Are you sure you want to reset the chat? This action can’t be undone.</p>
            <div className="modal-actions">
              <button className="btn btn-light" onClick={handleCloseResetModal} disabled={onResetChatLoading}>
                Cancel
              </button>
              <button className="btn btn-alert" onClick={confirmResetChat} disabled={onResetChatLoading}>
                {onResetChatLoading ? <Loader /> : null} Reset
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ChatbotHeader;
